import { useEffect, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { CATEGORIES } from "../../data/content";

const ImgUpload = ({ label, url, onUploaded, testid }) => {
  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success(`${label} photo uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-24 rounded-sm overflow-hidden border border-white/10 bg-obsidian-700 flex items-center justify-center shrink-0">
        {url ? <img src={url} alt={label} className="h-full w-full object-cover" /> : <span className="font-mono text-[9px] text-zinc-600 uppercase">{label}</span>}
      </div>
      <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-sm border border-neon/40 px-3 py-2 text-xs font-bold text-neon hover:bg-neon hover:text-obsidian transition-colors">
        <Upload size={13} /> {label}
        <input type="file" accept="image/*" className="hidden" data-testid={testid}
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
    </div>
  );
};

export default function AdminProjects({ onChange }) {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    const r = await api.patch(`/admin/projects/${id}`, body);
    setProjects((ps) => ps.map((p) => (p.id === id ? r.data : p)));
    return r.data;
  };
  const save = async (p) => {
    const { id, ...body } = p;
    await patch(id, body);
    toast.success("Project saved");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    setProjects((ps) => ps.filter((p) => p.id !== id));
    onChange?.();
    toast.success("Project deleted");
  };
  const add = async () => {
    const r = await api.post("/admin/projects", { title: "New Project", category: "basketball", location: "Sydney, NSW", summary: "" });
    setProjects((ps) => [r.data, ...ps]);
    setOpen(r.data.id);
    onChange?.();
    toast.success("Project created — fill in the details");
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex justify-end mb-5">
        <button onClick={add} data-testid="add-project-btn"
          className="inline-flex items-center gap-2 rounded-sm bg-neon px-5 py-2.5 text-sm font-bold text-obsidian hover:bg-neon-hover transition-colors">
          <Plus size={16} /> Add Project
        </button>
      </div>
      <div className="space-y-4">
        {projects.map((p) => (
          <div key={p.id} className="rounded-lg border border-white/10 bg-obsidian-800" data-testid={`admin-project-${p.id}`}>
            <button onClick={() => setOpen(open === p.id ? null : p.id)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
              <div className="flex items-center gap-4 min-w-0">
                <div className="h-12 w-16 rounded-sm overflow-hidden bg-obsidian-700 shrink-0">
                  {p.after_image && <img src={p.after_image} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <p className="font-bold truncate">{p.title}</p>
                  <p className="font-mono text-[10px] uppercase text-zinc-500">{p.category} · {p.location}</p>
                </div>
              </div>
              <span className="font-mono text-[10px] text-zinc-500 shrink-0">{open === p.id ? "CLOSE" : "EDIT"}</span>
            </button>

            {open === p.id && (
              <div className="border-t border-white/10 p-5 space-y-4">
                <div className="flex flex-wrap gap-6">
                  <ImgUpload label="Before" url={p.before_image} testid={`upload-before-${p.id}`}
                    onUploaded={(url) => patch(p.id, { before_image: url })} />
                  <ImgUpload label="After" url={p.after_image} testid={`upload-after-${p.id}`}
                    onUploaded={(url) => patch(p.id, { after_image: url, needs_real_media: false })} />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {[["title", "Title"], ["location", "Location"], ["court_type", "Court type"], ["surface", "Surface"], ["colours", "Colours"], ["line_marking", "Line marking"]].map(([k, ph]) => (
                    <input key={k} defaultValue={p[k]} placeholder={ph} onChange={(e) => (p[k] = e.target.value)}
                      className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                  ))}
                  <select defaultValue={p.category} onChange={(e) => (p.category = e.target.value)}
                    className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon capitalize">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <textarea defaultValue={p.summary} placeholder="Summary" rows={2} onChange={(e) => (p.summary = e.target.value)}
                  className="w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon" />
                <div className="flex justify-between">
                  <button onClick={() => del(p.id)} data-testid={`delete-project-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-red-400"><Trash2 size={15} /> Delete</button>
                  <button onClick={() => save(p)} data-testid={`save-project-${p.id}`}
                    className="rounded-sm bg-neon px-5 py-2.5 text-sm font-bold text-obsidian">Save Project</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
